import React, { FC, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  FilterSectionData,
  FiltersSectionProps,
} from "@shared/features/filters/types";
import { useFilter } from "@shared/features/filters/useFilter";
import CheckboxFiltersSection from "common/Filters/FiltersColumn/CheckboxFiltersSection";
import SliderFiltersSection from "common/Filters/SliderFiltersSection";
import PriceRangeFiltersSection from "common/Filters/PriceRangeFiltersSection";
import MultiSelectFiltersSection from "common/Filters/MultiSelectFiltersSection";
import TimeRangeFiltersSection from "common/Filters/common/TimeRangeFiltersSection";
import BooleanFiltersSection from "common/Filters/FiltersColumn/BooleanFiltersSection";
import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import LinkButton from "common/Buttons/LinkButton";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import {
  FILTER_TYPES_DATA,
  FILTERS_HELPER_TOOLTIP_DATA,
} from "@shared/features/filters/constants";
import {
  FilterSectionType,
  FilterType,
  AnalyticsPageName,
  LanguageNameSpaces,
} from "@shared/types";
import TagsFiltersSection from "common/Filters/FiltersColumn/TagsFiltersSection";
import { useBottomNavigator } from "utils/hooks/misc";
import Text from "common/Typography/Text";
import Switch from "common/Buttons/Switch.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const FiltersSection = ({
  filterType,
  filtersActions,
  filtersSelectors,
  filterTypeToTitle,
}: FiltersSectionProps) => {
  const [sectionHovered, setSectionHovered] = useState(false);
  const filterSectionData = useFilter({
    filterType,
    filtersActions,
    filtersSelectors,
  });
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();

  const FilterComponent =
    FILTER_SECTION_TYPE_TO_COMPONENT[filterSectionData.filterSectionType];
  if (
    !filterSectionData.title ||
    !FilterComponent ||
    filterSectionData?.shouldHide
  )
    return null;

  const filterTypeToAdditionalProps: {
    [key in FilterType]?: any;
  } = {
    [FilterType.TIME_FRAME]: {
      style: { margin: "4px 0 8px 0" },
    },
  };

  const filterTypeTooltipData = FILTERS_HELPER_TOOLTIP_DATA[filterType];
  const helperTooltipData = filterTypeTooltipData
    ? {
        ...filterTypeTooltipData,
        description: t(filterTypeTooltipData?.description),
      }
    : undefined;
  const resetButtonData = filterSectionData.resetButtonData;
  const toggleData = filterSectionData.toggleData;

  const toggle = toggleData?.show ? (
    <ToggleWrapper>
      <Switch
        checked={toggleData.isChecked}
        onClick={toggleData.onChange}
        size="small"
      />
      <ToggleText>{toggleData.text}</ToggleText>
    </ToggleWrapper>
  ) : undefined;

  if (isNewLook) {
    return (
      <NewLookWrapper
        onMouseEnter={() => setSectionHovered(true)}
        onMouseLeave={() => setSectionHovered(false)}
        id={`filter-${FILTER_TYPES_DATA[filterType].queryKey}`}
      >
        <NewLookTitleRow>
          <NewLookSectionTitle>
            {filterTypeToTitle?.[filterType]
              ? t(filterTypeToTitle?.[filterType])
              : filterSectionData.title}
            {helperTooltipData && (
              <HelperTooltip
                {...helperTooltipData}
                analyticsPageName={AnalyticsPageName.FILTERS}
                placement="bottomRight"
              />
            )}
          </NewLookSectionTitle>
          {!resetButtonData.shouldHide &&
            (sectionHovered || isBottomNavigator) && (
              <StyledButton type="link" onClick={resetButtonData.onClick}>
                {resetButtonData.resetText}
              </StyledButton>
            )}
        </NewLookTitleRow>
        {toggle}
        <FilterComponent
          {...filterSectionData}
          {...filterTypeToAdditionalProps[filterType]}
        />
      </NewLookWrapper>
    );
  }

  return (
    <Wrapper
      onMouseEnter={() => setSectionHovered(true)}
      onMouseLeave={() => setSectionHovered(false)}
      id={`filter-${FILTER_TYPES_DATA[filterType].queryKey}`}
    >
      {toggle}
      <Flex style={{ marginBottom: 6 }}>
        <Title
          type="secondary"
          level={4}
          style={{ display: "flex", alignItems: "center" }}
        >
          {filterTypeToTitle?.[filterType]
            ? t(filterTypeToTitle?.[filterType])
            : filterSectionData.title}
          {helperTooltipData && (
            <HelperTooltip
              {...helperTooltipData}
              analyticsPageName={AnalyticsPageName.FILTERS}
              placement="bottomRight"
            />
          )}
        </Title>
        {!resetButtonData.shouldHide &&
          (sectionHovered || isBottomNavigator) && (
            <StyledButton type="link" onClick={resetButtonData.onClick}>
              {resetButtonData.resetText}
            </StyledButton>
          )}
      </Flex>
      <FilterComponent
        {...filterSectionData}
        {...filterTypeToAdditionalProps[filterType]}
      />
    </Wrapper>
  );
};

export default FiltersSection;

const FILTER_SECTION_TYPE_TO_COMPONENT: {
  [key in FilterSectionType]?: FC<FilterSectionData<any>>;
} = {
  [FilterSectionType.MULTI_SELECT]: MultiSelectFiltersSection,
  [FilterSectionType.CHECKBOX]: CheckboxFiltersSection,
  [FilterSectionType.RADIO]: CheckboxFiltersSection,
  [FilterSectionType.SLIDER]: SliderFiltersSection,
  [FilterSectionType.PRICE_RANGE]: PriceRangeFiltersSection,
  [FilterSectionType.TIME_RANGE]: TimeRangeFiltersSection,
  [FilterSectionType.BOOLEAN]: BooleanFiltersSection,
  [FilterSectionType.TAGS]: TagsFiltersSection,
};

const Wrapper = styled.div`
  width: 100%;
  &:first-of-type {
    border-top: ${(p) => p.theme.borders.light};
    padding-top: 16px;
  }
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.light};
    padding-bottom: 12px;
    margin-bottom: 16px;
  }
`;
const NewLookWrapper = styled.div`
  width: 100%;
  padding: 16px 0 12px;
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.lightest};
  }
`;
const NewLookTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 22px;
  margin-bottom: 6px;
`;
const NewLookSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  .anticon,
  svg {
    color: ${(p) => p.theme.colors.secondaryText};
  }
`;
const StyledButton = styled(LinkButton)`
  padding: 0;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  height: 10px;
  display: flex;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    `
    &, span {
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    }
  `}
`;
const ToggleWrapper = styled(Flex)`
  justify-content: flex-start;
  margin-bottom: 8px;
`;
const ToggleText = styled(Text)`
  margin-left: 8px;
`;
